import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

// @mui
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EmailIcon from '@mui/icons-material/Email'

// MD
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'

// Layout
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Loader from 'examples/Loader'

// Services
import { useAppServices } from 'hook/services'

const initials = (first = '', last = '') =>
  `${first?.[0] || ''}${last?.[0] || ''}`.toUpperCase() || '?'

const monthOptions = (() => {
  const now = new Date()
  const out = []
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1))
    const value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
    const label = d.toLocaleString('default', { month: 'long', year: 'numeric' })
    out.push({ value, label })
  }
  return out
})()

const formatMonthLabel = (key) => {
  const [y, m] = key.split('-')
  const d = new Date(Date.UTC(Number(y), Number(m) - 1, 1))
  return d.toLocaleString('default', { month: 'short', year: '2-digit' })
}

// A self-contained bar chart so we don't need to bring in a chart library.
// Two bars per month — view (purple) and email_click (orange).
function MiniBarChart({ data }) {
  const max = data.reduce((m, r) => Math.max(m, r.view, r.email_click), 0) || 1
  return (
    <MDBox
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))`,
        gap: 1,
        alignItems: 'end',
        height: 200,
        padding: 1,
      }}
    >
      {data.map((row) => (
        <MDBox
          key={row.month}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100%',
            justifyContent: 'flex-end',
          }}
        >
          <MDBox
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'flex-end',
              height: '100%',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <MDBox
              title={`Views: ${row.view}`}
              sx={{
                width: '40%',
                height: `${(row.view / max) * 100}%`,
                backgroundColor: '#65558F',
                borderRadius: '4px 4px 0 0',
                minHeight: row.view > 0 ? 4 : 0,
              }}
            />
            <MDBox
              title={`Email clicks: ${row.email_click}`}
              sx={{
                width: '40%',
                height: `${(row.email_click / max) * 100}%`,
                backgroundColor: '#E8A04B',
                borderRadius: '4px 4px 0 0',
                minHeight: row.email_click > 0 ? 4 : 0,
              }}
            />
          </MDBox>
          <MDTypography
            variant="caption"
            sx={{ mt: 0.5, fontSize: 10, color: 'text.secondary' }}
          >
            {formatMonthLabel(row.month)}
          </MDTypography>
        </MDBox>
      ))}
    </MDBox>
  )
}

function StatCard({ icon, label, value, color }) {
  return (
    <Card sx={{ p: 2 }}>
      <MDBox display="flex" alignItems="center" gap={2}>
        <MDBox
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: color,
          }}
        >
          {icon}
        </MDBox>
        <MDBox>
          <MDTypography variant="caption" color="text" sx={{ display: 'block' }}>
            {label}
          </MDTypography>
          <MDTypography variant="h4" fontWeight="bold">
            {value}
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  )
}

function CreativeDirectoryAnalyticsDetail() {
  const AppService = useAppServices()
  const navigate = useNavigate()
  const { id } = useParams()

  const [data, setData] = useState(null)
  const [loader, setLoader] = useState(true)
  const [month, setMonth] = useState(monthOptions[0].value)

  const fetchDetail = async (m = month) => {
    setLoader(true)
    try {
      const params = new URLSearchParams({ month: m })
      const { response } = await AppService.creativeDirectory.getAnalyticsByIdAdmin({
        params: { id },
        query: params.toString(),
      })
      if (response?.success) {
        setData(response.data)
      } else {
        setData(null)
      }
    } catch (err) {
      console.error('❌ Error fetching analytics detail:', err)
      setData(null)
    } finally {
      setLoader(false)
    }
  }

  useEffect(() => {
    fetchDetail(month)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, month])

  if (loader) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Loader />
      </DashboardLayout>
    )
  }

  if (!data?.listing) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} textAlign="center">
          <MDTypography variant="h6">Listing not found.</MDTypography>
        </MDBox>
      </DashboardLayout>
    )
  }

  const { listing, monthly, total, trend } = data
  const profileImage = listing.user?.profile_image
  const email = listing.user?.email

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          {/* Header card */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                  <IconButton onClick={() => navigate('/agency/creative-directory/analytics')}>
                    <ArrowBackIcon />
                  </IconButton>
                  <MDTypography variant="h5" fontWeight="medium">
                    Listing analytics
                  </MDTypography>
                </MDBox>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
                  <Avatar
                    src={profileImage}
                    sx={{ width: 72, height: 72, bgcolor: '#FFEEDF', color: '#835414' }}
                  >
                    {initials(listing.firstName, listing.lastName)}
                  </Avatar>
                  <MDBox flex={1}>
                    <MDTypography variant="h5" fontWeight="bold">
                      {listing.firstName} {listing.lastName}
                    </MDTypography>
                    <MDTypography variant="button" color="text">
                      {listing.studioName ? `${listing.studioName} · ` : ''}
                      {listing.profession} · {listing.city}
                    </MDTypography>
                    <MDBox display="flex" gap={1} mt={1}>
                      <Chip
                        size="small"
                        label={listing.tier}
                        color={listing.tier === 'featured' ? 'warning' : 'info'}
                        sx={{ textTransform: 'uppercase', fontSize: 10 }}
                      />
                      <Chip
                        size="small"
                        label={listing.status}
                        sx={{ textTransform: 'uppercase', fontSize: 10 }}
                      />
                      {email ? (
                        <Chip size="small" label={email} variant="outlined" />
                      ) : null}
                    </MDBox>
                  </MDBox>
                  <TextField
                    select
                    size="small"
                    label="Month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    sx={{ minWidth: 200 }}
                  >
                    {monthOptions.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </MDBox>
            </Card>
          </Grid>

          {/* Stat cards — selected month */}
          <Grid item xs={12} md={3}>
            <StatCard
              icon={<VisibilityIcon sx={{ color: 'white' }} />}
              label={`Views — ${monthOptions.find((m) => m.value === month)?.label}`}
              value={monthly.view}
              color="#65558F"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={<EmailIcon sx={{ color: 'white' }} />}
              label={`Email clicks — ${monthOptions.find((m) => m.value === month)?.label}`}
              value={monthly.email_click}
              color="#E8A04B"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={<VisibilityIcon sx={{ color: 'white' }} />}
              label="Total views (all-time)"
              value={total.view}
              color="#544679"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={<EmailIcon sx={{ color: 'white' }} />}
              label="Total email clicks (all-time)"
              value={total.email_click}
              color="#C9A26A"
            />
          </Grid>

          {/* 12-month trend */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDTypography variant="h6" fontWeight="medium">
                    Last 12 months
                  </MDTypography>
                  <MDBox display="flex" gap={2}>
                    <MDBox display="flex" alignItems="center" gap={0.5}>
                      <MDBox
                        sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: '#65558F' }}
                      />
                      <MDTypography variant="caption" color="text">
                        Views
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center" gap={0.5}>
                      <MDBox
                        sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: '#E8A04B' }}
                      />
                      <MDTypography variant="caption" color="text">
                        Email clicks
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
                <Divider sx={{ mb: 2 }} />
                <MiniBarChart data={trend} />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  )
}

export default CreativeDirectoryAnalyticsDetail
